import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { from, map, Observable, shareReplay, switchMap } from 'rxjs';

import { MiddlewareSDK, SDK } from '@plugins/middleware-sdk';

type LanguageHeaders = {
  acceptLanguage: string;
  contentLanguage: string;
};

let languageHeadersCache$: Observable<LanguageHeaders> | undefined;

export const resetLanguageHeadersCache = (): void => {
  languageHeadersCache$ = undefined;
};

const isServerApiRequest = (req: HttpRequest<unknown>): boolean => {
  const requestUrl = new URL(req.url, window.location.href);
  return (
    requestUrl.origin === window.location.origin &&
    requestUrl.pathname.startsWith('/api')
  );
};

const getLanguageHeaders$ = (
  sdk: MiddlewareSDK,
): Observable<LanguageHeaders> => {
  if (languageHeadersCache$) {
    return languageHeadersCache$;
  }

  languageHeadersCache$ = from(
    Promise.all([sdk.getBrowserLanguage(), sdk.getPreferredLanguage()]),
  ).pipe(
    map(([{ lang, locale }, contentLocale]) => ({
      acceptLanguage: locale ? `${lang}-${locale},${lang}` : lang,
      contentLanguage: contentLocale.replace('_', '-'),
    })),
    shareReplay(1),
  );

  return languageHeadersCache$;
};

export const interceptLanguageHeaders = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  sdk: MiddlewareSDK,
): Observable<HttpEvent<unknown>> => {
  if (!isServerApiRequest(req)) {
    return next(req);
  }

  const hasAcceptLanguage = req.headers.has('Accept-Language');
  const hasContentLanguage = req.headers.has('X-Content-Language');

  if (hasAcceptLanguage && hasContentLanguage) {
    return next(req);
  }

  return getLanguageHeaders$(sdk).pipe(
    switchMap(({ acceptLanguage, contentLanguage }) => {
      const setHeaders: Record<string, string> = {};

      if (!hasAcceptLanguage) {
        setHeaders['Accept-Language'] = acceptLanguage;
      }

      if (!hasContentLanguage) {
        setHeaders['X-Content-Language'] = contentLanguage;
      }

      return next(
        req.clone({
          setHeaders,
        }),
      );
    }),
  );
};

export const languageHeadersInterceptorFn: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const sdk = inject<MiddlewareSDK>(SDK);
  return interceptLanguageHeaders(req, next, sdk);
};
